import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';
/**
 * EMZI NEXUS V6 - Inbox API Client
 * Frontend API client for Internal Inbox System
 */

/**
 * Get inbox threads for current user
 */
export const getInboxThreads = async (filters = {}) => {
  const { type, status, search, page = 1, limit = 50 } = filters;
  
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (status) params.append('status', status);
  if (search) params.append('search', search);
  params.append('page', page);
  params.append('limit', limit);
  
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/list?${params}`,
    {
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch inbox');
  }
  
  return response.json();
};

/**
 * Get unread counts by category
 */
export const getUnreadCounts = async () => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/unread-count`,
    {
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch unread counts');
  }
  
  return response.json();
};

/**
 * Get single thread detail
 */
export const getThread = async (threadId) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/${threadId}`,
    {
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch thread');
  }
  
  return response.json();
};

/**
 * Mark threads as read
 */
export const markAsRead = async (threadIds) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/mark-read`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ thread_ids: threadIds })
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to mark as read');
  }
  
  return response.json();
};

/**
 * Send manual message
 */
export const sendMessage = async (messageData) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/send`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(messageData)
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to send message');
  }
  
  return response.json();
};

/**
 * Create approval request (Executor → Supervisor)
 */
export const createApprovalRequest = async (requestData) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/auto/approval-request`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to create approval request');
  }
  
  return response.json();
};

/**
 * Approve request
 */
export const approveRequest = async (threadId, notes = null) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/${threadId}/action/approve`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action: 'approve', notes })
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to approve request');
  }
  
  return response.json();
};

/**
 * Request changes
 */
export const requestChanges = async (threadId, notes) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/${threadId}/action/request-changes`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ action: 'reject', notes })
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to request changes');
  }
  
  return response.json();
};

/**
 * Resolve thread
 */
export const resolveThread = async (threadId) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/${threadId}/resolve`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to resolve thread');
  }
  
  return response.json();
};

/**
 * Send reminders
 */
export const sendReminders = async (threadIds, message = null) => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/remind`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ thread_ids: threadIds, message })
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to send reminders');
  }
  
  return response.json();
};

/**
 * Get inbox statistics
 */
export const getInboxStats = async () => {
  const response = await fetch(
    `${BACKEND_URL}/api/inbox/stats/overview`,
    {
      headers: {
        'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        'Content-Type': 'application/json'
      }
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch inbox stats');
  }
  
  return response.json();
};
